import { useState } from "react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { LayoutDashboard, Smartphone, Building2, ListChecks, CheckSquare, Link2, Users, KeyRound, FileText, User, LogOut, ChevronRight, MessageSquare, FileBarChart2 as FileText2, Webhook, TrendingUp, Landmark, FlaskConical, Globe, Menu, Send } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { canAccessPage } from "@/lib/rbac";
import { useTxnNotifications, NotifBell, NotifToast } from "@/hooks/useTxnNotifications";
import NotificationsBell from "@/components/NotificationsBell";
import MobileNav from "@/components/MobileNav";
import BackendVersionBadge from "@/components/BackendVersionBadge";
import DeploySyncBanner from "@/components/DeploySyncBanner";

const NAV = [
    { to: "/partner", icon: LayoutDashboard, label: "Dashboard", end: true, perm: "dashboard" },
    { to: "/partner/upi", icon: Smartphone, label: "UPI Channels", perm: "upi" },
    { to: "/partner/banks", icon: Building2, label: "Bank Accounts", perm: "banks" },
    { to: "/partner/transactions", icon: ListChecks, label: "Transactions", perm: "transactions" },
    { to: "/partner/success", icon: CheckSquare, label: "Successful Txns", perm: "success_txns" },
    { to: "/partner/approvals", icon: CheckSquare, label: "Approvals", perm: "approvals" },
    { to: "/partner/customers", icon: Users, label: "Customers", perm: "customers" },
    { to: "/partner/sms", icon: MessageSquare, label: "SMS Verify", perm: "sms" },
    { to: "/partner/statements", icon: FileText2, label: "Statements", perm: "statements" },
    { to: "/partner/aa", icon: Landmark, label: "Bank Aggregator", gated: "aa_enabled", perm: "aa" },
    { to: "/partner/link", icon: Link2, label: "Universal Link", perm: "link" },
    { to: "/partner/links", icon: Link2, label: "Payment Links", perm: "links" },
    { to: "/partner/websites", icon: Globe, label: "Websites", perm: "links" },
    { to: "/partner/experiments", icon: FlaskConical, label: "A/B Experiments", perm: "links" },
    { to: "/partner/analytics", icon: TrendingUp, label: "Analytics", perm: "analytics" },
    { to: "/partner/alerts", icon: Webhook, label: "Alerts", perm: "alerts" },
    // Team management is admin-only — no `perm` so partner_team filter rejects it.
    { to: "/partner/team", icon: Users, label: "Team", adminOnly: true },
    { to: "/partner/credentials", icon: KeyRound, label: "API Credentials", perm: "credentials" },
    { to: "/partner/webhooks", icon: Webhook, label: "Webhooks", perm: "webhooks" },
    { to: "/partner/invoices", icon: FileText, label: "Invoices", perm: "invoices" },
    { to: "/partner/payouts", icon: Send, label: "Payouts", always: true },
    { to: "/partner/panel", icon: Smartphone, label: "FirstPay Panel", always: true },
    // Profile is always visible — every authenticated user can view their account.
    { to: "/partner/profile", icon: User, label: "Profile", always: true },
];

export default function PartnerShell() {
    const { user, logout } = useAuth();
    const nav = useNavigate(); const loc = useLocation();
    const [mobileOpen, setMobileOpen] = useState(false);
    const onLogout = async () => { await logout(); nav("/partner/login"); };
    const crumb = NAV.find((n) => (n.end ? loc.pathname === n.to : loc.pathname.startsWith(n.to)))?.label || "Dashboard";
    const visibleNav = NAV.filter((it) => canAccessPage(user, it));
    const { latest, soundOn, setSound, pushOn, setPush, clear } = useTxnNotifications({ enabled: !!user });
    return (
        <div className="flex min-h-screen bg-[#FAFBFC]">
            <aside className="hidden lg:flex flex-col w-60 border-r border-slate-200 bg-white sticky top-0 h-screen">
                <div className="px-5 py-5 border-b border-slate-100">
                    <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500 to-blue-600 flex items-center justify-center text-white font-bold text-sm">P</div>
                        <div>
                            <div className="font-bold text-sm leading-tight">PayGateway</div>
                            <div className="text-[10px] text-slate-500 uppercase tracking-wider mt-0.5 truncate max-w-[140px]">{user?.partner?.business_name || "Partner"}</div>
                        </div>
                    </div>
                </div>
                <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
                    {visibleNav.map((it) => (
                        <NavLink data-testid={`nav-partner-${it.label.toLowerCase().replace(/\s/g, "")}`} key={it.to} to={it.to} end={it.end}
                            className={({ isActive }) => `flex items-center gap-2.5 px-3 py-2 rounded-md text-sm transition-colors ${
                                isActive ? "bg-emerald-50 text-emerald-700 font-medium" : "text-slate-600 hover:bg-slate-50"
                            }`}>
                            <it.icon className="w-4 h-4" />
                            {it.label}
                        </NavLink>
                    ))}
                </nav>
                <div className="p-3 border-t border-slate-100">
                    <div className="px-3 py-2 mb-1">
                        <div className="text-xs text-slate-400">Signed in</div>
                        <div className="text-sm font-medium truncate">{user?.email}</div>
                    </div>
                    <button data-testid="partner-logout" onClick={onLogout} className="w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-sm text-slate-600 hover:bg-slate-50">
                        <LogOut className="w-4 h-4" /> Sign out
                    </button>
                </div>
            </aside>
            <main className="flex-1 min-w-0">
                <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
                    <div className="px-4 sm:px-6 py-3 flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2 min-w-0">
                            {/* Hamburger — visible only below `lg`. Opens
                                the mobile drawer. Sized to be tap-friendly
                                on small screens (44px hit area). */}
                            <button
                                data-testid="partner-mobile-menu"
                                onClick={() => setMobileOpen(true)}
                                aria-label="Open navigation"
                                className="lg:hidden p-2 -ml-1.5 rounded-md text-slate-600 hover:bg-slate-100"
                            >
                                <Menu className="w-5 h-5" />
                            </button>
                            <div className="text-sm text-slate-500 flex items-center gap-1.5 min-w-0">
                                <span className="hidden sm:inline">Partner</span>
                                <ChevronRight className="w-3.5 h-3.5 hidden sm:inline" />
                                <span className="font-medium text-slate-900 truncate">{crumb}</span>
                            </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                            <BackendVersionBadge />
                            <NotificationsBell />
                            <NotifBell soundOn={soundOn} setSound={setSound} pushOn={pushOn} setPush={setPush} />
                            <span className="pill pill-success">{user?.role === "partner_admin" ? "Admin" : "Team"}</span>
                        </div>
                    </div>
                </header>
                <DeploySyncBanner />
                <div className="p-4 sm:p-6 fade-up"><Outlet /></div>
            </main>
            {/* Mobile-only drawer. Same nav items as the sticky desktop
                aside above — driven from the visibleNav array so RBAC
                stays in sync automatically. */}
            <MobileNav
                open={mobileOpen}
                onClose={() => setMobileOpen(false)}
                navItems={visibleNav}
                variant="partner"
                user={user}
                onLogout={onLogout}
                testIdPrefix="partner"
                partnerBusinessName={user?.partner?.business_name}
            />
            <NotifToast
                latest={latest}
                onDismiss={clear}
                onView={() => { clear(); nav("/partner/approvals"); }}
            />
        </div>
    );
}
